<template>
  <div class="musubi-page musubi-page--compliance">
    <header class="musubi-page__head">
      <h1>Compliance</h1>
      <p class="text-muted">
        Your compliance home: your Legitimate Interests Assessments, sending policy,
        suppression health, and the lawful basis of your subscriber base — plus the
        GDPR audit surfaces (disclosures, suppressions, erasures) below.
      </p>
    </header>

    <!-- Persistent "not legal advice" strip — on every compliance screen (decision a) -->
    <div class="legal-strip">
      <CygnusIcon name="shield" />
      <span><strong>This is documentation, not legal advice.</strong> Yorlin structures and stores the decisions <strong>you</strong> make so you have defensible paperwork on file. It doesn't tell you whether your outreach is lawful — for that, get a professional review.</span>
    </div>

    <!-- Compliance home dashboard (mockup §4) -->
    <section v-if="home" class="home-grid">
      <!-- LIA status -->
      <div class="hcard">
        <div class="hcard__h"><CygnusIcon name="shield" /> LIA status
          <span v-if="home.lia.review_due > 0" class="review-chip warn"><CygnusIcon name="clock" /> {{ home.lia.review_due }} review due</span>
        </div>
        <template v-if="home.entitled">
          <div class="status-big">
            <div class="status-big__ring" :class="home.lia.review_due > 0 ? 'warn' : (home.lia.on_file > 0 ? 'ok' : 'warn')">
              <CygnusIcon :name="home.lia.on_file > 0 ? 'check' : 'plus'" />
            </div>
            <div>
              <div class="status-big__t">{{ home.lia.on_file }} assessment{{ home.lia.on_file === 1 ? '' : 's' }} on file</div>
              <div class="status-big__m">
                <template v-if="home.lia.on_file === 0">No Legitimate Interests Assessment yet. Create one to document your legitimate-interest outreach.</template>
                <template v-else-if="home.lia.review_due > 0">One or more assessments are due for their annual review soon.</template>
                <template v-else>All assessments are current.</template>
              </div>
            </div>
          </div>
          <ul v-if="home.lia.assessments.length" class="lia-list">
            <li v-for="a in home.lia.assessments" :key="a.id">
              <a :href="`/admin/compliance/lia/${a.id}`">{{ a.title || 'Untitled' }} <span class="v">v{{ a.version }}</span></a>
            </li>
          </ul>
          <div class="card-cta">
            <a href="/admin/compliance/lia/new" class="btn btn-primary btn-sm"><CygnusIcon name="plus" class="me-1" />New assessment</a>
          </div>
        </template>
        <template v-else>
          <div class="teaser-mini">
            <div class="upgrade__eyebrow">Growth+</div>
            <p>The guided LIA wizard, the "PECR-checked" badge, and a versioned document store are available on Growth+. Your mechanical safeguards and this dashboard's suppression/basis stats are available on every plan.</p>
            <a href="/admin/usage-plans" class="btn lia-btn-violet btn-sm"><CygnusIcon name="bolt" class="me-1" />Upgrade to Growth+</a>
          </div>
        </template>
      </div>

      <!-- Sending policy -->
      <div class="hcard">
        <div class="hcard__h"><CygnusIcon name="envelope" /> Sending policy</div>
        <div class="pol-row"><CygnusIcon :name="home.sending.dkim_sender ? 'check' : 'minus'" :class="home.sending.dkim_sender ? 'text-teal' : 'text-muted'" /><span>Sender identity DKIM-verified</span><span class="v">{{ home.sending.dkim_sender || 'not set' }}</span></div>
        <div class="pol-row"><CygnusIcon name="check" class="text-teal" /><span>One-click unsubscribe header on every outreach email</span><span class="v">enforced</span></div>
        <div class="pol-row"><CygnusIcon name="check" class="text-teal" /><span>Cross-tenant volume guard</span><span class="v">active</span></div>
        <div class="card-cta"><a href="/outreach/deliverability" class="hlink">View deliverability <CygnusIcon name="arrow-right" /></a></div>
      </div>

      <!-- Suppression stats -->
      <div class="hcard">
        <div class="hcard__h"><CygnusIcon name="shield" class="text-teal" /> Suppression</div>
        <div class="mini-stats">
          <div><div class="mini-stat__v">{{ home.suppression.suppressed.toLocaleString() }}</div><div class="mini-stat__l">Suppressed</div></div>
          <div><div class="mini-stat__v">{{ home.suppression.this_month }}</div><div class="mini-stat__l">Last 30 days</div></div>
          <div><div class="mini-stat__v">{{ home.suppression.sends_respecting }}%</div><div class="mini-stat__l">Sends respecting it</div></div>
        </div>
        <div class="card-cta"><button class="hlink hlink--btn" @click="selectTab('suppressions')">Open suppression list <CygnusIcon name="arrow-right" /></button></div>
        <p class="small text-muted mb-0">Opt-outs are honoured for that sender; bounces &amp; complaints are suppressed platform-wide.</p>
      </div>

      <!-- Basis breakdown donut -->
      <div class="hcard">
        <div class="hcard__h"><CygnusIcon name="chart-bar" /> Basis of your subscriber base</div>
        <div class="donut-wrap">
          <div class="donut" :style="donutStyle">
            <div class="donut__c"><span class="n">{{ home.basis.total.toLocaleString() }}</span><span class="l">subscribers</span></div>
          </div>
          <div class="legend">
            <div class="legend__row"><span class="legend__sw li"></span><span class="legend__k">Legitimate interest</span><span class="legend__v">{{ pct(home.basis.legitimate_interest) }} · {{ home.basis.legitimate_interest }}</span></div>
            <div class="legend__row"><span class="legend__sw consent"></span><span class="legend__k">Consent</span><span class="legend__v">{{ pct(home.basis.consent) }} · {{ home.basis.consent }}</span></div>
            <div class="legend__row" :class="{ flag: home.basis.none > 0 }"><span class="legend__sw unknown"></span><span class="legend__k">No basis recorded</span><span class="legend__v">{{ pct(home.basis.none) }} · {{ home.basis.none }}</span></div>
          </div>
        </div>
        <div v-if="home.basis.none > 0" class="card-cta"><a :href="home.basis.fix_route" class="hlink">Fix the {{ home.basis.none }} without a basis <CygnusIcon name="arrow-right" /></a></div>
      </div>
    </section>

    <!-- Badge mode setting (blocking opt-in — Growth+) -->
    <section v-if="home && home.entitled" class="badge-mode-card">
      <div class="badge-mode-card__body">
        <div>
          <div class="hcard__h" style="margin-bottom:6px"><CygnusIcon name="gauge" /> "PECR-checked" badge mode</div>
          <p class="small text-muted mb-0" style="max-width:640px">
            <strong>Advisory</strong> (default) shows the badge and a fix link but never blocks a send — you remain the data controller.
            <strong>Blocking</strong> stops a launch server-side when a mechanical check fails, until you fix it.
          </p>
        </div>
        <div class="badge-mode-toggle">
          <button class="mode-btn" :class="{ active: home.badge_mode === 'advisory' }" :disabled="savingMode" @click="setBadgeMode('advisory')">Advisory</button>
          <button class="mode-btn" :class="{ active: home.badge_mode === 'blocking' }" :disabled="savingMode" @click="setBadgeMode('blocking')">Blocking</button>
        </div>
      </div>
    </section>

    <!-- Send safety slice 5: the workspace's own do-not-contact list. -->
    <DoNotContactPanel />

    <h5 class="audit-heading">GDPR audit surfaces</h5>

    <div class="tabs-bar">
      <button
        v-for="t in tabs"
        :key="t.key"
        type="button"
        class="btn btn-sm tabs-bar__btn"
        :class="tab === t.key ? 'btn-primary' : 'btn-outline-secondary'"
        @click="selectTab(t.key)"
      >
        {{ t.label }}
        <span v-if="state(t.key).loaded" class="badge bg-light text-dark ms-1">{{ state(t.key).total }}</span>
      </button>
    </div>

    <!-- Disclosures -->
    <section v-if="tab === 'disclosures'" class="tab-panel">
      <p class="small text-muted tab-panel__explainer">
        Art.14 requires telling people, within a reasonable period, whose data was shown to
        someone who didn't collect it directly from them — this is the log of which companies'
        person data this workspace has been shown.
      </p>
      <div class="tab-panel__bar">
        <button class="btn btn-link btn-sm p-0" :disabled="disclosures.busy" @click="load('disclosures', true)">
          <CygnusIcon name="spinner" :spin="true" v-if="disclosures.busy" />
          <span v-else><CygnusIcon name="refresh" /> Refresh</span>
        </button>
      </div>
      <MusubiTabulator
        :rows="disclosures.rows"
        :columns="disclosuresColumns"
        storage-key="admin.compliance.disclosures"
        :initial-sort="{ column: 'last_disclosed_at', dir: 'desc' }"
        :page-size="25"
        :loading="disclosures.busy"
        :error="disclosures.error"
      />
      <p v-if="disclosures.loaded && disclosures.total > disclosures.rows.length" class="small text-muted mt-2">
        Showing 500 of {{ disclosures.total }}
      </p>
    </section>

    <!-- Suppressions -->
    <section v-if="tab === 'suppressions'" class="tab-panel">
      <p class="small text-muted tab-panel__explainer">
        Do-not-send list — addresses are stored hashed, never in the clear. Opt-outs are honoured
        immediately for that sender only; bounced or complaining addresses are protected
        platform-wide (a bad address on shared infrastructure hurts everyone); and a cross-tenant
        volume guard stops any one address being flooded. The <strong>Scope</strong> column shows
        which applies: <em>platform</em> (every workspace) or <em>this workspace only</em>.
      </p>
      <div class="tab-panel__bar">
        <button class="btn btn-link btn-sm p-0" :disabled="suppressions.busy" @click="load('suppressions', true)">
          <CygnusIcon name="spinner" :spin="true" v-if="suppressions.busy" />
          <span v-else><CygnusIcon name="refresh" /> Refresh</span>
        </button>
      </div>
      <MusubiTabulator
        :rows="suppressions.rows"
        :columns="suppressionsColumns"
        storage-key="admin.compliance.suppressions"
        :initial-sort="{ column: 'created', dir: 'desc' }"
        :page-size="25"
        :loading="suppressions.busy"
        :error="suppressions.error"
      />
      <p v-if="suppressions.loaded && suppressions.total > suppressions.rows.length" class="small text-muted mt-2">
        Showing 500 of {{ suppressions.total }}
      </p>
    </section>

    <!-- Erasures -->
    <section v-if="tab === 'erasures'" class="tab-panel">
      <p class="small text-muted tab-panel__explainer">
        Subscribers who exercised their GDPR right to erasure — the underlying record is
        anonymised and kept only as a tombstone to prevent re-import.
      </p>
      <div class="tab-panel__bar">
        <button class="btn btn-link btn-sm p-0" :disabled="erasures.busy" @click="load('erasures', true)">
          <CygnusIcon name="spinner" :spin="true" v-if="erasures.busy" />
          <span v-else><CygnusIcon name="refresh" /> Refresh</span>
        </button>
      </div>
      <MusubiTabulator
        :rows="erasures.rows"
        :columns="erasuresColumns"
        storage-key="admin.compliance.erasures"
        :initial-sort="{ column: 'erased_at', dir: 'desc' }"
        :page-size="25"
        :loading="erasures.busy"
        :error="erasures.error"
      />
      <p v-if="erasures.loaded && erasures.total > erasures.rows.length" class="small text-muted mt-2">
        Showing 500 of {{ erasures.total }}
      </p>
    </section>

    <!-- Full disclaimer footer (decision a) -->
    <div class="disclaimer">
      <CygnusIcon name="shield" />
      <span><strong>Yorlin helps you keep good records — it isn't your lawyer.</strong> The assessments, checks and badges here document the decisions you've made and the safeguards this platform enforces. They don't constitute legal advice and don't guarantee that any particular campaign complies with PECR, UK GDPR or any other law. For a definitive view on your specific situation, get a professional review.</span>
    </div>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";
import MusubiTabulator from "@root/components/MusubiTabulator.vue";
import { esc, safeUrl } from "./util/cellHtml";

const ENDPOINTS = {
  disclosures: '/api/v1/admin/compliance/disclosures',
  suppressions: '/api/v1/admin/compliance/suppressions',
  erasures: '/api/v1/admin/compliance/erasures',
};

const REASON_BADGE = {
  erasure: 'warning',
  complained: 'danger',
  hard_bounce: 'secondary',
  spamtrap: 'dark',
};

function fmtDate(v) {
  return v ? new Date(v * 1000).toLocaleDateString() : '—';
}

export default {
  name: 'AdminCompliance',
  components: { MusubiTabulator },
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      tab: 'disclosures',
      tabs: [
        { key: 'disclosures', label: 'Disclosures' },
        { key: 'suppressions', label: 'Suppressions' },
        { key: 'erasures', label: 'Erasures' },
      ],
      disclosures: { rows: [], total: 0, loaded: false, busy: false, error: null },
      suppressions: { rows: [], total: 0, loaded: false, busy: false, error: null },
      erasures: { rows: [], total: 0, loaded: false, busy: false, error: null },
      home: null,
      savingMode: false,
    };
  },
  computed: {
    donutStyle() {
      const b = this.home?.basis;
      if (!b || !b.total) return { background: '#CBD2DE' };
      const li = (b.legitimate_interest / b.total) * 100;
      const co = (b.consent / b.total) * 100;
      const p1 = li.toFixed(2);
      const p2 = (li + co).toFixed(2);
      return { background: `conic-gradient(#14B8A6 0 ${p1}%, #4F46E5 ${p1}% ${p2}%, #CBD2DE ${p2}% 100%)` };
    },
    disclosuresColumns() {
      return [
        { key: 'company_name', label: 'Company',
          format: (v, row) => esc(v) || `Company #${esc(row.company_id)}` },
        { key: 'website', label: 'Website',
          format: (v) => v
            ? `<a href="${safeUrl(v)}" target="_blank" rel="noopener">${esc(v)}</a>`
            : '<span class="text-muted">—</span>' },
        { key: 'first_disclosed_at', label: 'First disclosed', width: 150, sorter: 'number', format: fmtDate },
        { key: 'last_disclosed_at', label: 'Last disclosed', width: 150, sorter: 'number', format: fmtDate },
      ];
    },
    suppressionsColumns() {
      const cols = [
        { key: 'email_hash', label: 'Hash', format: (v) => `<code>${esc(v)}</code>` },
        { key: 'reason', label: 'Reason', width: 130,
          format: (v) => {
            const cls = REASON_BADGE[v] || 'secondary';
            const dark = cls === 'warning' ? ' text-dark' : '';
            return `<span class="badge bg-${cls}${dark}">${esc(v) || '—'}</span>`;
          } },
        { key: 'scope', label: 'Scope', width: 150,
          format: (v) => {
            const platform = v !== 'tenant';
            const cls = platform ? 'bg-danger' : 'bg-info text-dark';
            const label = platform ? 'Every workspace' : 'This workspace only';
            return `<span class="badge ${cls}">${label}</span>`;
          } },
      ];
      // source_tenant_id is operator-only — the API omits it for tenant-scoped
      // callers, so only render the column when the payload actually carries it.
      if (this.suppressions.rows.some((r) => 'source_tenant_id' in r)) {
        cols.push({ key: 'source_tenant_id', label: 'Source tenant', width: 140,
          format: (v) => v ? `#${esc(v)}` : '<span class="text-muted">—</span>' });
      }
      cols.push({ key: 'created', label: 'Added', width: 130, sorter: 'number', format: fmtDate });
      return cols;
    },
    erasuresColumns() {
      return [
        { key: 'id', label: 'Subscriber ID', width: 130, sorter: 'number' },
        { key: 'erased_at', label: 'Erased on', width: 150, sorter: 'number', format: fmtDate },
        { key: 'created', label: 'Originally created', width: 170, sorter: 'number', format: fmtDate },
      ];
    },
  },
  mounted() {
    this.load('disclosures');
    this.loadHome();
  },
  methods: {
    async loadHome() {
      try {
        const res = await this.api.get('/api/v1/admin/compliance/home');
        this.home = res && res.basis !== undefined ? res : (res?.data ?? res);
      } catch (e) { this.home = null; }
    },
    pct(n) {
      const t = this.home?.basis?.total || 0;
      return t > 0 ? `${Math.round((n / t) * 100)}%` : '0%';
    },
    async setBadgeMode(mode) {
      if (!this.home || this.home.badge_mode === mode) return;
      this.savingMode = true;
      try {
        const res = await this.api.put('/api/v1/admin/compliance/settings', { badge_mode: mode });
        const body = res?.data ?? res;
        this.home.badge_mode = body?.badge_mode || mode;
      } catch (e) { /* keep prior mode on failure */ }
      this.savingMode = false;
    },
    state(key) {
      return this[key];
    },
    selectTab(key) {
      this.tab = key;
      this.load(key);
    },
    async load(key, force = false) {
      const s = this[key];
      if (!s || s.busy) return;
      if (s.loaded && !force) return;
      s.busy = true; s.error = null;
      try {
        const res = await this.api.get(ENDPOINTS[key]);
        const body = (res && res.total !== undefined) ? res : (res?.data ?? res);
        s.rows = Array.isArray(body?.rows) ? body.rows : [];
        s.total = Number(body?.total) || s.rows.length;
        s.loaded = true;
      } catch (e) {
        s.error = e?.response?.data?.error || e?.message || `Failed to load ${key}.`;
      }
      s.busy = false;
    },
  },
};
</script>

<style>
.musubi-page--compliance { padding: var(--m-space-6, 32px); }
.musubi-page--compliance .tabs-bar { display: flex; gap: 8px; margin-bottom: 16px; }
.musubi-page--compliance .tab-panel__explainer { margin-bottom: 10px; }
.musubi-page--compliance .tab-panel__bar { display: flex; justify-content: flex-end; margin-bottom: 8px; }
.musubi-page--compliance code { background: var(--m-bg-subtle, #EEF2FF); color: var(--m-text-primary, #0F172A); padding: 1px 5px; border-radius: var(--m-radius, 4px); font-size: 0.82em; }

/* ── compliance home dashboard (Growth Feature 5) ── */
.musubi-page--compliance .legal-strip { display:flex; align-items:center; gap:11px; padding:10px 18px; background:linear-gradient(180deg,#FBFAFF,#F7F5FF); border:1px solid #E0D2FB; border-radius:10px; font-size:.82rem; color:#4B3A73; line-height:1.4; margin-bottom:18px; }
.musubi-page--compliance .legal-strip i { color:#7C3AED; }
.musubi-page--compliance .home-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:16px; margin-bottom:16px; }
@media(max-width:900px){ .musubi-page--compliance .home-grid{ grid-template-columns:1fr; } }
.musubi-page--compliance .hcard { background:#fff; border:1px solid #EAECF3; border-radius:14px; box-shadow:0 1px 3px rgba(15,23,42,.05); padding:18px 20px; }
.musubi-page--compliance .hcard__h { display:flex; align-items:center; gap:9px; font-size:.72rem; font-weight:800; text-transform:uppercase; letter-spacing:.05em; color:#697086; margin-bottom:14px; }
.musubi-page--compliance .hcard__h i { color:#4F46E5; }
.musubi-page--compliance .hcard__h i.text-teal, .musubi-page--compliance .text-teal { color:#14B8A6; }
.musubi-page--compliance .review-chip { margin-left:auto; display:inline-flex; align-items:center; gap:6px; font-size:.68rem; font-weight:700; padding:3px 9px; border-radius:999px; }
.musubi-page--compliance .review-chip.warn { background:#FFF4E6; color:#9A5B12; border:1px solid #F0DCBB; }
.musubi-page--compliance .status-big { display:flex; align-items:center; gap:13px; }
.musubi-page--compliance .status-big__ring { width:46px; height:46px; border-radius:50%; display:flex; align-items:center; justify-content:center; flex:none; font-size:1.1rem; }
.musubi-page--compliance .status-big__ring.ok { background:#E4F7F4; color:#0F766E; border:2px solid #B6EBE4; }
.musubi-page--compliance .status-big__ring.warn { background:#FFF4E6; color:#9A5B12; border:2px solid #F0DCBB; }
.musubi-page--compliance .status-big__t { font-size:1.05rem; font-weight:700; }
.musubi-page--compliance .status-big__m { font-size:.79rem; color:#697086; margin-top:2px; line-height:1.4; }
.musubi-page--compliance .lia-list { list-style:none; padding:0; margin:12px 0 0; font-size:.84rem; }
.musubi-page--compliance .lia-list li { padding:4px 0; }
.musubi-page--compliance .lia-list a { color:#4338CA; text-decoration:none; }
.musubi-page--compliance .lia-list .v { color:#9AA0AC; font-size:.75rem; }
.musubi-page--compliance .card-cta { margin-top:15px; display:flex; gap:10px; align-items:center; flex-wrap:wrap; }
.musubi-page--compliance .hlink { font-size:.8rem; font-weight:700; color:#4338CA; text-decoration:none; display:inline-flex; align-items:center; gap:5px; background:none; border:none; padding:0; cursor:pointer; }
.musubi-page--compliance .hlink:hover { text-decoration:underline; }
.musubi-page--compliance .mini-stats { display:flex; gap:22px; }
.musubi-page--compliance .mini-stat__v { font-size:1.5rem; font-weight:700; }
.musubi-page--compliance .mini-stat__l { font-size:.7rem; color:#697086; text-transform:uppercase; letter-spacing:.04em; margin-top:2px; }
.musubi-page--compliance .pol-row { display:flex; align-items:center; gap:10px; padding:8px 0; border-bottom:1px solid #EAECF3; font-size:.84rem; }
.musubi-page--compliance .pol-row:last-of-type { border-bottom:none; }
.musubi-page--compliance .pol-row .v { margin-left:auto; font-weight:600; font-size:.79rem; color:#697086; }
.musubi-page--compliance .donut-wrap { display:flex; align-items:center; gap:20px; }
.musubi-page--compliance .donut { width:110px; height:110px; border-radius:50%; flex:none; position:relative; }
.musubi-page--compliance .donut::after { content:""; position:absolute; inset:18px; background:#fff; border-radius:50%; }
.musubi-page--compliance .donut__c { position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center; z-index:1; }
.musubi-page--compliance .donut__c .n { font-size:1.2rem; font-weight:700; }
.musubi-page--compliance .donut__c .l { font-size:.58rem; text-transform:uppercase; letter-spacing:.04em; color:#9AA0AC; }
.musubi-page--compliance .legend { display:grid; gap:9px; flex:1; }
.musubi-page--compliance .legend__row { display:flex; align-items:center; gap:9px; font-size:.83rem; }
.musubi-page--compliance .legend__sw { width:11px; height:11px; border-radius:3px; flex:none; }
.musubi-page--compliance .legend__sw.li { background:#14B8A6; }
.musubi-page--compliance .legend__sw.consent { background:#4F46E5; }
.musubi-page--compliance .legend__sw.unknown { background:#CBD2DE; }
.musubi-page--compliance .legend__v { margin-left:auto; color:#697086; font-weight:600; }
.musubi-page--compliance .legend__row.flag .legend__k { color:#9A5B12; }
.musubi-page--compliance .teaser-mini { background:linear-gradient(135deg,#F3EEFE,#FBFAFF); border:1px solid #E0D2FB; border-radius:12px; padding:16px; }
.musubi-page--compliance .teaser-mini p { font-size:.84rem; color:#697086; line-height:1.5; margin:6px 0 12px; }
.musubi-page--compliance .upgrade__eyebrow { font-size:.66rem; font-weight:800; letter-spacing:.08em; text-transform:uppercase; color:#7C3AED; }
.musubi-page--compliance .lia-btn-violet { background:#7C3AED; border:1px solid #7C3AED; color:#fff; text-decoration:none; }
.musubi-page--compliance .lia-btn-violet:hover { background:#6D28D9; color:#fff; }
.musubi-page--compliance .badge-mode-card { background:#fff; border:1px solid #EAECF3; border-radius:14px; padding:16px 20px; margin-bottom:20px; }
.musubi-page--compliance .badge-mode-card__body { display:flex; gap:16px; align-items:center; justify-content:space-between; flex-wrap:wrap; }
.musubi-page--compliance .badge-mode-toggle { display:flex; border:1px solid #E0E3EC; border-radius:8px; overflow:hidden; flex:none; }
.musubi-page--compliance .mode-btn { border:none; background:#fff; padding:8px 16px; font-size:.82rem; font-weight:600; color:#697086; cursor:pointer; }
.musubi-page--compliance .mode-btn.active { background:#4F46E5; color:#fff; }
.musubi-page--compliance .audit-heading { font-size:.9rem; font-weight:700; color:#697086; margin:8px 0 12px; }
.musubi-page--compliance .disclaimer { margin-top:26px; padding:16px 20px; background:linear-gradient(180deg,#FBFAFF,#F8F6FF); border:1px solid #E0D2FB; border-radius:12px; display:flex; gap:13px; align-items:flex-start; font-size:.82rem; line-height:1.6; color:#4B3A73; }
.musubi-page--compliance .disclaimer i { color:#7C3AED; flex:none; margin-top:2px; }
</style>
